/**
 * Manager Billing — landlord subscription billing boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/billing.schemas.ts` imports
 * and re-exports these to register the routes, and `apps/landlord/src/api/billing.ts` derives
 * its `BundleTemplate` / `SubscriptionStatus` / `BillingAddress` / `Invoice` types from the
 * same objects. See packages/api-types/README.md.
 *
 * This is the landlord subscription-billing schema family — distinct from the admin-facing
 * `billing-admin.schemas.ts` (a separate migration).
 *
 * Covers:
 *   GET  /landlord/api/billing/bundles              (BundleTemplateSchema, within `bundles`)
 *   POST /landlord/api/billing/subscribe            (SubscribeRequestSchema, SubscribeResponseSchema)
 *   GET  /landlord/api/billing/subscription         (SubscriptionStatusResponseSchema)
 *   GET  /landlord/api/billing/invoices             (InvoicesListResponseSchema; ?limit & ?cursor, see InvoicesListQuerySchema)
 *   GET  /landlord/api/billing/invoices/:invoiceId/pdf
 *   POST /landlord/api/billing/redeem-coupon        (BundleTemplateSchema, within CouponValidationResponseSchema)
 *
 * Plan feature copy is not part of these responses: it is the code-defined PLAN_COPY in
 * `@resida/api-types/plan-copy` (BR-113).
 */
import { z } from 'zod';
import { isoDateTimeString } from '../shared/date.js';
/**
 * Schema for billing address (used in subscribe request for paid bundles)
 */
export const BillingAddressSchema = z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    state: z.string().regex(/^[A-Z]{2}$/, 'State must be a 2-letter uppercase code'),
    postalCode: z.string().min(1),
    country: z.string().length(2, 'Country must be an ISO 3166-1 alpha-2 code (e.g. "US")'),
});
/**
 * Schema for subscribe request (POST /landlord/api/billing/subscribe)
 */
export const SubscribeRequestSchema = z.object({
    bundleTemplateId: z.string().min(1),
    couponCode: z.string().optional(),
    billingAddress: BillingAddressSchema.optional(),
    /** Confirm the saved default card server-side instead of collecting a new one (BR-112). */
    useSavedCard: z.boolean().optional(),
});
/**
 * Schema for Phase 1 subscribe response (SetupIntent created)
 * Returns a SetupIntent client secret and reserved subscriptionId — no DynamoDB writes.
 */
export const SubscribeResponseSchema = z.object({
    subscriptionId: z.string(),
    setupIntentClientSecret: z.string(),
    customerId: z.string(),
    /** Present only when the saved card was used (BR-112): the server-confirmed SetupIntent. */
    savedCard: z
        .object({
        setupIntentId: z.string(),
        status: z.enum(['succeeded', 'requires_action']),
    })
        .optional(),
});
/**
 * Schema for bundle template
 */
export const BundleTemplateSchema = z.object({
    id: z.string(),
    name: z.string(),
    unitPriceCents: z.number(),
    durationDays: z.number().nullable(),
    isPublic: z.boolean(),
    isCurrent: z.boolean(),
    // Iteration 16 — snapshot prices; null = not offered (BR-89), 0 = free.
    listingFeeCents: z.number().nullable(),
    listingPeriodDays: z.number().nullable(),
    agentUnitPriceCents: z.number().nullable(),
});
/** What an invoice bills for (DC-19); shared by the landlord and admin invoice responses. */
export const InvoiceKindSchema = z.enum(['subscription', 'listing_fee', 'agent_proration']);
/**
 * Schema for invoice in responses
 */
export const InvoiceSchema = z.object({
    id: z.uuid(),
    stripeInvoiceId: z.string().nullable(),
    amountCents: z.number(),
    subtotalExcludingTaxCents: z.number().optional(),
    taxCents: z.number().optional(),
    taxBehavior: z.string().optional(),
    status: z.enum(['pending', 'paid', 'failed', 'void', 'uncollectible']),
    dueDate: z.string(),
    createdAt: z.string(),
    invoiceKind: InvoiceKindSchema,
    description: z.string().nullable(),
});
/** GET /landlord/api/billing/invoices query: newest-first pages (limit default 50, max 100). */
export const InvoicesListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().min(1).max(2048).optional(),
});
/** One page of invoices; `nextCursor` (pass back as `cursor`) is null on the last page. */
export const InvoicesListResponseSchema = z.object({
    invoices: z.array(InvoiceSchema),
    nextCursor: z.string().nullable(),
});
/**
 * Why the agent support add-on cannot be enabled (BR-100). `null` when it can.
 */
export const AgentAddonIneligibleReasonSchema = z.enum([
    'trial',
    'zero_price_bundle',
    'not_offered',
    'not_active',
    'already_enabled',
]);
/** Saved default card (brand + last4 only — never a PAN, expiry or fingerprint). */
export const DefaultPaymentMethodSchema = z.object({
    brand: z.string(),
    last4: z.string(),
});
/**
 * Schema for subscription status response
 */
export const SubscriptionStatusResponseSchema = z.object({
    hasActiveSubscription: z.boolean(),
    /**
     * The billing account is known to be deactivated. False for "unknown" too (no account yet, no entitling
     * subscription mid-checkout, a failed billing read): only this flag may say the account is deactivated.
     */
    accountDeactivated: z.boolean(),
    status: z.string().nullable(),
    bundleName: z.string().nullable(),
    unitCount: z.number(),
    unitPriceCents: z.number(),
    nextBillingDate: z.string().nullable(),
    currentPeriodEnd: z.string().nullable(),
    trialDaysRemaining: z.number().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    graceDeadline: z.string().nullable(),
    // Iteration 16 — snapshot prices of the current bundle (null = not offered).
    listingFeeCents: z.number().nullable(),
    listingPeriodDays: z.number().nullable(),
    agentUnitPriceCents: z.number().nullable(),
    agentAddon: z.object({
        status: z.string(),
        eligible: z.boolean(),
        ineligibleReason: AgentAddonIneligibleReasonSchema.nullable(),
    }),
    defaultPaymentMethod: DefaultPaymentMethodSchema.nullable(),
    estimatedNextInvoiceCents: z.number().nullable(),
});
// ---------------------------------------------------------------------------
// Default payment method — POST /landlord/api/billing/payment-method/{setup-intent,confirm}
// ---------------------------------------------------------------------------
export const PaymentMethodSetupIntentRequestSchema = z.object({
    billingAddress: BillingAddressSchema,
});
export const PaymentMethodSetupIntentResponseSchema = z.object({
    clientSecret: z.string(),
});
export const PaymentMethodConfirmRequestSchema = z.object({
    setupIntentId: z.string().min(1),
});
export const PaymentMethodConfirmResponseSchema = z.object({
    defaultPaymentMethod: DefaultPaymentMethodSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Agent support add-on — /landlord/api/billing/agent-addon/* (DC-49)
// ---------------------------------------------------------------------------
export const AgentAddonQuoteResponseSchema = z.object({
    eligible: z.boolean(),
    ineligibleReason: AgentAddonIneligibleReasonSchema.optional(),
    proratedCents: z.number().int().nonnegative(),
    unitPriceCents: z.number().int().nonnegative(),
    unitCount: z.number().int().nonnegative(),
    /** unitCount x unitPriceCents. */
    monthlyCents: z.number().int().nonnegative(),
    periodStart: isoDateTimeString.nullable(),
    periodEnd: isoDateTimeString.nullable(),
    daysRemaining: z.number().int().nonnegative(),
    nextBillingDate: isoDateTimeString.nullable(),
    defaultPaymentMethod: DefaultPaymentMethodSchema.nullable(),
    /**
     * True when this is the PINNED request of an earlier unresolved enable under the caller's
     * idempotency key (BR-102): the amount and dates are that attempt's, and the retry resumes it.
     */
    resumesEarlierAttempt: z.boolean(),
});
/** GET /landlord/api/billing/agent-addon/quote (query): the caller's persisted attempt key, if any. */
export const AgentAddonQuoteQuerySchema = z.object({
    idempotencyKey: z.uuid().optional(),
});
export const AgentAddonEnableRequestSchema = z.object({
    /** Must equal the server quote's proratedCents (NFR-SECURITY-7). */
    consentToProratedCents: z.number().int().nonnegative(),
    idempotencyKey: z.uuid(),
});
export const AgentAddonEnableResponseSchema = z.object({
    status: z.literal('active'),
    invoice: z.object({
        subtotalCents: z.number().int(),
        taxCents: z.number().int(),
        totalCents: z.number().int(),
    }),
});
/** Disable -> `cancelling` (with the effective date); undo-disable -> `active`. */
export const AgentAddonStatusResponseSchema = z.object({
    status: z.enum(['active', 'cancelling']),
    effectiveAt: isoDateTimeString.nullable().optional(),
});
/**
 * 402 / 403 / 409 body of the payment-bearing billing routes. `declineCode`, `quote` and
 * `ineligibleReason` sit at the top level (not inside `details`, which production strips).
 */
export const BillingPaymentErrorResponseSchema = z.object({
    error: z.string(),
    code: z.string(),
    message: z.string(),
    statusCode: z.number(),
    timestamp: z.string(),
    declineCode: z.string().optional(),
    quote: z.record(z.string(), z.unknown()).optional(),
    ineligibleReason: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Public pricing — GET /api/public/pricing (DC-50, unauthenticated)
// ---------------------------------------------------------------------------
export const PublicPricingResponseSchema = z.object({
    trial: z
        .object({
        name: z.string(),
        durationDays: z.number().nullable(),
        listingFeeCents: z.number().nullable(),
        listingPeriodDays: z.number().nullable(),
    })
        .nullable(),
    plans: z.array(z.object({
        id: z.string(),
        name: z.string(),
        unitPriceCents: z.number(),
        listingFeeCents: z.number().nullable(),
        listingPeriodDays: z.number().nullable(),
        agentUnitPriceCents: z.number().nullable(),
    })),
});
//# sourceMappingURL=billing.js.map